import type { FaceSize } from './metrics';
import type { CardPose } from './pose';

/**
 * **假透视的全部数学**。
 *
 * ## 它在做什么
 *
 * 把「一块 `w × h` 的平牌」按 `CardPose` 摆到画面上的一个**四边形**。
 * 牌本身没有任何 3D 结构，所以叫"假的"；但用的投影是真的透视投影，
 * 所以近大远小、梯形、斜切这些都会自然出现，不需要逐个参数去凑。
 *
 * ```
 *   模型空间                 旋转                   投影              屏幕
 *  (±w/2, ±h/2, 0)  ──Rz·Rx·Ry──▶  (x', y', z')  ──÷深度──▶  (X, Y)
 * ```
 *
 * ## 为什么不做成"直接给四个角"
 *
 * 因为那样调用方要自己想清楚四个角的坐标 —— 那就等于把透视算法
 * 抄到每个调用点上。现在调用方只说"绕 Y 转了 0.2 弧度"，
 * 四个角怎么算、近大远小怎么体现，全在这里。
 *
 * ## 为什么这个文件不 import Pixi
 *
 * 因为这是纯数学，**它必须能在 node 里单测**（`test/render/projection.test.ts`）。
 * 一旦 import 了 `pixi.js`，测试就得开浏览器，于是没人会去测它 ——
 * 而透视算错的表现（牌是歪的、翻面了、抖动）恰恰是最难靠肉眼定位的一类 bug。
 */

/** 四个角，顺时针，从左上开始。坐标相对**卡牌中心** */
export interface CardCorners {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly x3: number;
  readonly y3: number;
}

/**
 * 逐角点的硬保护：深度绝不允许 ≤ 0。
 *
 * `resolvePose` 已经把 `perspective` 卡在下限了，正常永远碰不到这里。
 * 留着是因为**万一碰到了，症状是牌整个翻面/炸开**，
 * 而那种画面完全看不出是哪一步算错的。宁可缩一下也不要翻面。
 */
const MIN_DEPTH = 1e-3;

/**
 * 算出一张牌**歪成什么形状** —— 四个角，**以卡牌中心为原点**。
 *
 * ## 这里刻意不碰「摆放」
 *
 * `pose.x` / `pose.y` / `pose.scale` 对返回值**没有任何影响** ——
 * 摆放交给显示对象的 `position` / `scale`。理由有两条：
 *
 * 1. 缩放走显示对象，将来挂上去的阴影、描边才会跟着一起缩放
 * 2. 位置走显示对象，`CardView` 才能被塞进别的容器里做布局
 *
 * ⚠️ 这两个分工**一开始写反过**：角点里加了 `x`/`y`，容器又 `position` 一次，
 * 于是每张牌都跑到了两倍远的地方。所以测试里专门钉住了
 * 「x/y/scale 不影响角点」这两条。
 *
 * 换句话讲：**这个函数算的是形状，不是位置。**
 *
 * ## 一个容易误判的现象：倾斜之后四个角的「重心」会偏
 *
 * 真透视下，远的那条边被压小了，所以四个角的**平均位置**会朝近边挪一点。
 * 比如 pitch = 0.45 时重心大约朝下偏 7% 的牌高 —— **这是对的，不是 bug**。
 * 想要"视觉重心不动"就得自己在 `x` / `y` 上补偿，通用层不替你猜。
 */
export function projectCorners(pose: CardPose, size: FaceSize, pad = 0): CardCorners {
  // ⚠️ `pad` 是**出血**：牌面纹理比牌本身大一圈，网格要铺满整块纹理，
  //    所以四角要按 pad 往外扩。但 `distance` 仍然按**牌**的尺寸算 ——
  //    否则出血一开，同样的 `perspective` 会给出不同的透视强度，
  //    等于每加一层出血就把所有姿势调一遍。
  const halfW = size.width / 2 + pad;
  const halfH = size.height / 2 + pad;

  const cosYaw = Math.cos(pose.yaw);
  const sinYaw = Math.sin(pose.yaw);
  const cosPitch = Math.cos(pose.pitch);
  const sinPitch = Math.sin(pose.pitch);
  const cosRoll = Math.cos(pose.roll);
  const sinRoll = Math.sin(pose.roll);

  // 相机在 +z 轴上、距离 d，看向原点。f = d 保证"不转的时候刚好是原尺寸"
  const distance = pose.perspective * Math.max(size.width, size.height);

  /** 一个模型点 → 屏幕点 */
  const project = (x: number, y: number): readonly [number, number] => {
    // ① 绕 Y：x' = x·cosA + z·sinA, z' = −x·sinA + z·cosA（z 初始为 0）
    const x1 = x * cosYaw;
    const z1 = -x * sinYaw;

    // ② 绕 X：y' = y·cosP − z·sinP, z' = y·sinP + z·cosP
    const y2 = y * cosPitch - z1 * sinPitch;
    const z2 = y * sinPitch + z1 * cosPitch;

    // ③ 绕 Z（平面内旋转）。注意 z 不变 —— 所以它转的是"牌自己的法线"
    const x3 = x1 * cosRoll - y2 * sinRoll;
    const y3 = x1 * sinRoll + y2 * cosRoll;

    // ④ 透视投影：相机在 +z 上，所以点到相机的距离是 (d − z)
    const depth = Math.max(distance - z2, MIN_DEPTH);
    const shrink = distance / depth;

    return [x3 * shrink, y3 * shrink];
  };

  // 屏幕坐标 y 向下为正，所以"左上"是 (−halfW, −halfH)
  const [x0, y0] = project(-halfW, -halfH); // 左上
  const [x1, y1] = project(halfW, -halfH); // 右上
  const [x2, y2] = project(halfW, halfH); // 右下
  const [x3, y3] = project(-halfW, halfH); // 左下

  return { x0, y0, x1, y1, x2, y2, x3, y3 };
}

/**
 * 这四个角是不是**还没歪**（一个正的矩形，边都平行于坐标轴）。
 *
 * 两处用它：
 * - 测试里断言"默认姿态 = 尺寸完全不变"
 * - 渲染时可以据此跳过 `setCorners`（省一次顶点缓冲上传）
 */
export function isAxisAligned(corners: CardCorners, epsilon = 1e-9): boolean {
  const near = (a: number, b: number): boolean => Math.abs(a - b) <= epsilon;
  return (
    near(corners.x0, corners.x3) && // 左边竖直
    near(corners.x1, corners.x2) && // 右边竖直
    near(corners.y0, corners.y1) && // 上边水平
    near(corners.y2, corners.y3) // 下边水平
  );
}

/** 四个角的平均位置。"中心有没有跑偏"的定量指标 */
export function cornersCenter(corners: CardCorners): readonly [number, number] {
  return [
    (corners.x0 + corners.x1 + corners.x2 + corners.x3) / 4,
    (corners.y0 + corners.y1 + corners.y2 + corners.y3) / 4,
  ];
}

/** 四个角的外接矩形。给命中测试 / 阴影尺寸用 */
export function cornersBounds(corners: CardCorners): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  const xs = [corners.x0, corners.x1, corners.x2, corners.x3];
  const ys = [corners.y0, corners.y1, corners.y2, corners.y3];
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}
